<template>
  <div class="page-container">
    <v-snackbar v-model="showSnackBar" top right :timeout="4000">
      {{ msg }}
      <template v-slot:action="{ attrs }">
        <v-btn icon small v-bind="attrs" @click="showSnackBar = false">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <PageHeader
      title="Payment Log"
      subtitle="Every payment attempt, with the reason a payment failed."
      icon="mdi-clipboard-text-clock-outline"
      :count="totalLength"
    ></PageHeader>

    <!-- Counts per status; they follow the search and dates, not the tab -->
    <div class="log-tabs">
      <button
        v-for="t in tabs"
        :key="t.key"
        type="button"
        class="log-tab"
        :class="[{ 'log-tab--active': status === t.key }, 'log-tab--' + t.tone]"
        @click="selectTab(t.key)"
      >
        <span class="log-tab-label">{{ t.label }}</span>
        <span class="log-tab-count">{{ tabCount(t.key) }}</span>
        <span v-if="t.key !== 'Attention'" class="log-tab-amount">₹{{ money(tabAmount(t.key)) }}</span>
        <span v-else class="log-tab-amount">check on eTreasury</span>
      </button>
    </div>

    <div class="surface">
      <div class="filter-bar">
        <v-text-field
          v-model="search"
          class="filter-bar-search"
          outlined
          dense
          hide-details
          clearable
          label="Department ID, GRN, CIN, name, application no. or production house"
          prepend-inner-icon="mdi-magnify"
          @input="searchChanged"
          @click:clear="clearSearch"
        ></v-text-field>
        <v-menu v-model="fromMenu" :close-on-content-click="false" max-width="290">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="formatDay(fromDate)"
              outlined
              dense
              hide-details
              readonly
              clearable
              label="From date"
              prepend-inner-icon="mdi-calendar-start"
              v-bind="attrs"
              v-on="on"
              @click:clear="fromDate = null; reload()"
            ></v-text-field>
          </template>
          <v-date-picker v-model="fromDate" :max="toDate || today" color="#15803d" @change="fromMenu = false; reload()"></v-date-picker>
        </v-menu>
        <v-menu v-model="toMenu" :close-on-content-click="false" max-width="290">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="formatDay(toDate)"
              outlined
              dense
              hide-details
              readonly
              clearable
              label="To date"
              prepend-inner-icon="mdi-calendar-end"
              v-bind="attrs"
              v-on="on"
              @click:clear="toDate = null; reload()"
            ></v-text-field>
          </template>
          <v-date-picker v-model="toDate" :min="fromDate" :max="today" color="#15803d" @change="toMenu = false; reload()"></v-date-picker>
        </v-menu>
        <v-btn v-if="hasFilters" depressed class="btn-act btn-act--neutral filter-bar-button" @click="clearFilters">
          <v-icon left small>mdi-filter-remove-outline</v-icon>
          Clear filters
        </v-btn>
      </div>

      <div v-if="loading" class="pa-4">
        <v-skeleton-loader type="table-row@5"></v-skeleton-loader>
      </div>

      <div v-else-if="!rows.length" class="empty-state">
        <v-icon size="52">mdi-cash-remove</v-icon>
        <div class="cell-strong">{{ hasFilters || status !== "all" ? "No payment attempts match these filters" : "No payment attempts yet" }}</div>
        <div>{{ hasFilters || status !== "all" ? "Change the search, dates or status tab." : "Attempts appear here as soon as an applicant starts a payment." }}</div>
      </div>

      <div v-else class="table-scroll">
        <v-simple-table class="app-table">
          <thead>
            <tr>
              <th>Started</th>
              <th>Application</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Reference</th>
              <th class="text-right">Details</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rows" :key="r._id">
              <td data-label="Started">
                <div>
                  <div class="cell-strong">{{ formatDay(r.created_at) }}</div>
                  <div class="cell-muted">{{ formatTime(r.created_at) }}</div>
                </div>
              </td>
              <td data-label="Application">
                <div>
                  <div class="cell-strong">{{ r.party_name || (r.appid && r.appid.applicantName) || "—" }}</div>
                  <div v-if="r.appid && r.appid.applicationNo" class="cell-muted">#{{ r.appid.applicationNo }}</div>
                  <div v-if="r.appid && r.appid.nameofProductionhouse" class="cell-muted">{{ r.appid.nameofProductionhouse }}</div>
                  <div v-if="r.appid && r.appid.divisionid && r.appid.divisionid.officeName" class="cell-muted">{{ r.appid.divisionid.officeName }}</div>
                </div>
              </td>
              <td data-label="Amount">
                <span class="pay-amount">₹{{ money(r.totalAmount) }}</span>
              </td>
              <td data-label="Status">
                <div class="status-cell">
                  <span class="status-pill" :class="'status-pill--' + tone(r)">{{ statusLabel(r) }}</span>
                  <div v-if="reasonOf(r)" class="fail-reason" :class="{ 'fail-reason--warn': isUnreadable(r) }">
                    <span class="fail-stage">{{ stageLabel(r.failureStage) }}</span>
                    <span class="fail-text">{{ reasonOf(r) }}</span>
                  </div>
                </div>
              </td>
              <td data-label="Reference">
                <div class="ref-list">
                  <div v-if="r.departmentId"><span class="ref-key">Dept ID</span><span class="ref-val">{{ r.departmentId }}</span></div>
                  <div v-if="r.GRN"><span class="ref-key">GRN</span><span class="ref-val">{{ r.GRN }}</span></div>
                  <div v-if="r.CIN"><span class="ref-key">CIN</span><span class="ref-val">{{ r.CIN }}</span></div>
                  <span v-if="!r.departmentId && !r.GRN && !r.CIN" class="cell-muted">—</span>
                </div>
              </td>
              <td class="app-table-actions text-right">
                <div class="row-actions">
                  <v-btn depressed small class="btn-act btn-act--sm btn-act--primary" @click="openDetails(r)">
                    <v-icon left small>mdi-text-box-search-outline</v-icon>
                    Details
                  </v-btn>
                </div>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>

      <div v-if="pages > 1" class="pagination-bar">
        <v-pagination v-model="page" :length="pages" :total-visible="7" color="#15803d"></v-pagination>
      </div>
    </div>

    <!-- One attempt in full -->
    <v-dialog v-model="detailOpen" max-width="860" scrollable>
      <v-card v-if="detail" class="detail-card">
        <v-card-title class="detail-title">
          <div>
            <div class="detail-heading">Payment attempt</div>
            <div class="detail-sub">{{ detail.departmentId || "No department ID" }}</div>
          </div>
          <v-spacer></v-spacer>
          <span class="status-pill" :class="'status-pill--' + tone(detail)">{{ statusLabel(detail) }}</span>
          <v-btn icon class="ml-2" @click="detailOpen = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="detail-body">
          <div v-if="reasonOf(detail)" class="fail-box" :class="{ 'fail-box--warn': isUnreadable(detail) }">
            <div class="fail-box-title">{{ stageLabel(detail.failureStage) }}</div>
            <div>{{ reasonOf(detail) }}</div>
            <div v-if="isUnreadable(detail)" class="fail-box-hint">
              The money may have been taken. Look the payment up on the eTreasury Search page (by department ID) before asking the applicant to pay again.
            </div>
          </div>

          <div class="detail-grid">
            <section>
              <h3>Fees</h3>
              <dl>
                <dt>Total</dt><dd>₹{{ money(detail.totalAmount) }}</dd>
                <dt>Shooting fee</dt><dd>₹{{ money(detail.shootingfee) }}</dd>
                <dt>Installation fee</dt><dd>₹{{ money(detail.installationfee) }}</dd>
                <template v-if="detail.AMOUNT"><dt>Amount reported by eTreasury</dt><dd>₹{{ money(detail.AMOUNT) }}</dd></template>
                <dt>Office code</dt><dd>{{ detail.officeCode || "—" }}</dd>
              </dl>
            </section>

            <section>
              <h3>Application</h3>
              <dl v-if="detail.appid">
                <dt>Application no.</dt><dd>{{ detail.appid.applicationNo || "—" }}</dd>
                <dt>Applicant</dt><dd>{{ detail.appid.applicantName || "—" }}</dd>
                <dt>Production house</dt><dd>{{ detail.appid.nameofProductionhouse || "—" }}</dd>
                <dt>Nature</dt><dd>{{ detail.appid.natureOfApplication || "—" }}</dd>
                <dt>Division</dt><dd>{{ (detail.appid.divisionid && detail.appid.divisionid.officeName) || "—" }}</dd>
                <dt>Application payment</dt><dd>{{ detail.appid.paymentStatus || "—" }}</dd>
              </dl>
              <div v-else class="cell-muted">The application no longer exists.</div>
              <v-btn v-if="detail.appid && detail.appid._id" depressed small class="btn-act btn-act--sm btn-act--primary mt-2" @click="openApplication(detail)">
                <v-icon left small>mdi-eye-outline</v-icon>
                Open application
              </v-btn>
            </section>

            <section>
              <h3>Payer</h3>
              <dl>
                <dt>Name</dt><dd>{{ detail.party_name || "—" }}</dd>
                <dt>PAN</dt><dd>{{ detail.pan_no || "—" }}</dd>
                <dt>Mobile</dt><dd>{{ detail.mobnumber || "—" }}</dd>
                <dt>PIN code</dt><dd>{{ detail.pin_no || "—" }}</dd>
                <dt>Address</dt><dd>{{ detail.address || "—" }}</dd>
                <template v-if="detail.isNamudekeralam"><dt>Source</dt><dd>Nammude Keralam</dd></template>
              </dl>
            </section>

            <section>
              <h3>eTreasury reply</h3>
              <dl v-if="hasGatewayReply(detail)">
                <dt>Gateway status</dt><dd>{{ gatewayStatusLabel(detail.gatewayStatus) }}</dd>
                <dt>GRN</dt><dd class="mono">{{ detail.GRN || "—" }}</dd>
                <dt>CIN</dt><dd class="mono">{{ detail.CIN || "—" }}</dd>
                <dt>Bank reference</dt><dd class="mono">{{ detail.BANKREFNO || "—" }}</dd>
                <dt>Bank code</dt><dd>{{ detail.BANK_CODE || "—" }}</dd>
                <dt>Bank time</dt><dd>{{ detail.BANKTIMESTAMP || "—" }}</dd>
                <dt>Remark status</dt><dd>{{ detail.REMARK_STATUS || "—" }}</dd>
                <dt>Remarks</dt><dd>{{ detail.REMARKS || "—" }}</dd>
              </dl>
              <div v-else class="cell-muted">eTreasury has not replied for this attempt.</div>
            </section>

            <section v-if="detail.refundStatus && detail.refundStatus !== 'NoRefund'">
              <h3>Refund</h3>
              <dl>
                <dt>Status</dt><dd>{{ detail.refundStatus }}</dd>
                <dt>Amount</dt><dd>₹{{ money(detail.refundAmount) }}</dd>
                <dt>Request number</dt><dd class="mono">{{ detail.RefundRequestNumber || "—" }}</dd>
                <dt>Date</dt><dd>{{ formatDateTime(detail.refundDate) || "—" }}</dd>
              </dl>
            </section>

            <section>
              <h3>Timeline</h3>
              <ol class="timeline">
                <li v-for="step in timeline(detail)" :key="step.label" :class="{ 'timeline--bad': step.bad }">
                  <span class="timeline-label">{{ step.label }}</span>
                  <span class="timeline-time">{{ step.at }}</span>
                </li>
              </ol>
            </section>
          </div>

          <!-- Everything that went to and came back from eTreasury, to analyse the attempt -->
          <div class="log-head">
            <h3 class="log-head-title">eTreasury log</h3>
            <v-btn depressed small class="btn-act btn-act--sm btn-act--neutral" :disabled="!etreasuryLog" @click="downloadLog">
              <v-icon left small>mdi-download-outline</v-icon>
              Download JSON
            </v-btn>
          </div>

          <div v-if="logLoading" class="pa-2"><v-skeleton-loader type="paragraph, paragraph"></v-skeleton-loader></div>
          <div v-else-if="logError" class="cell-muted">{{ logError }}</div>
          <div v-else-if="etreasuryLog" class="log-blocks">
            <section v-for="b in logBlocks" :key="b.key" class="log-block">
              <div class="log-block-head">
                <h4>{{ b.title }}</h4>
                <v-btn v-if="b.text" small text class="log-copy" @click="copy(b.text)">
                  <v-icon left small>mdi-content-copy</v-icon>
                  Copy
                </v-btn>
              </div>
              <pre v-if="b.text" class="raw">{{ b.text }}</pre>
              <div v-else class="cell-muted log-empty">{{ b.empty }}</div>
            </section>

            <section class="log-block">
              <div class="log-block-head">
                <h4>eTreasury's answer when asked about this payment</h4>
                <v-btn small text class="log-copy" v-if="lookupText" @click="copy(lookupText)">
                  <v-icon left small>mdi-content-copy</v-icon>
                  Copy
                </v-btn>
                <v-btn depressed small class="btn-act btn-act--sm btn-act--primary" :loading="lookupLoading" @click="askEtreasury">
                  <v-icon left small>mdi-bank-outline</v-icon>
                  {{ etreasuryLog.lookup ? "Ask again" : "Ask eTreasury now" }}
                </v-btn>
              </div>
              <pre v-if="lookupText" class="raw">{{ lookupText }}</pre>
              <div v-else class="cell-muted log-empty">
                Not asked yet. This calls eTreasury live, using the GRN or department ID, and is not stored.
              </div>
            </section>
          </div>

          <v-expansion-panels v-if="detail.failureDetails || detail.notes" flat class="detail-panels" multiple>
            <v-expansion-panel v-if="detail.failureDetails">
              <v-expansion-panel-header>Technical details of the failure</v-expansion-panel-header>
              <v-expansion-panel-content><pre class="raw">{{ detail.failureDetails }}</pre></v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel v-if="detail.notes">
              <v-expansion-panel-header>Notes</v-expansion-panel-header>
              <v-expansion-panel-content><pre class="raw">{{ detail.notes }}</pre></v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import PageHeader from "@/components/Common/PageHeader.vue";

const PAGE_SIZE = 10;

const STATUS_TONE = { Success: "success", Failed: "danger", Initiated: "warning", Cancelled: "neutral", Refundable: "info" };
const STATUS_LABEL = {
  Success: "Paid",
  Failed: "Failed",
  Initiated: "Waiting for payment",
  Cancelled: "Replaced by a newer attempt",
  Refundable: "Refundable",
};
// Where in the payment an attempt went wrong (Backend/controllers/paymentFailureLog.js)
const STAGE_LABEL = {
  Validation: "Could not start",
  Authentication: "eTreasury sign-in failed",
  ChallanGeneration: "Challan not generated",
  Gateway: "Declined by eTreasury",
  CallbackUnreadable: "Reply could not be read",
  Exception: "Server error",
};

// Counts and totals per status are for the whole search; the tab only picks the list.
const TABS = [
  { key: "all", label: "All", tone: "neutral" },
  { key: "Success", label: "Paid", tone: "success" },
  { key: "Failed", label: "Failed", tone: "danger" },
  { key: "Initiated", label: "Waiting", tone: "warning" },
  { key: "Cancelled", label: "Replaced", tone: "neutral" },
  { key: "Refundable", label: "Refundable", tone: "info" },
  { key: "Attention", label: "Needs checking", tone: "danger" },
];

// Super admin only: every payment attempt and why a failed one failed.
// Search, dates, status tab and paging are all done by the server.
export default {
  name: "PaymentLog",
  components: { PageHeader },
  data() {
    return {
      rows: [],
      totalLength: 0,
      counts: {},
      status: "all",
      loading: false,
      search: "",
      fromDate: null,
      toDate: null,
      fromMenu: false,
      toMenu: false,
      page: 1,
      msg: "",
      showSnackBar: false,
      searchTimer: null,
      requestNo: 0,
      today: moment().format("YYYY-MM-DD"),
      detailOpen: false,
      detail: null,
      // The raw eTreasury material of the open attempt (see /admin/payment/log/detail)
      etreasuryLog: null,
      logLoading: false,
      logError: "",
      lookupLoading: false,
    };
  },
  computed: {
    // The blocks of the eTreasury log, in the order the payment happened
    logBlocks() {
      const log = this.etreasuryLog;
      if (!log) return [];
      const callbacks = log.callbackReceived || [];
      return [
        {
          key: "request",
          title: "Request body posted to eTreasury",
          text: log.requestPosted ? this.pretty(log.requestPosted) : "",
          empty: "Nothing was posted: this attempt failed before the challan was made.",
        },
        {
          key: "callback",
          title: "Callback body received from eTreasury (raw, still encrypted)",
          text: callbacks.length ? this.pretty(callbacks.length === 1 ? callbacks[0] : callbacks) : "",
          empty: "eTreasury has not posted anything back for this attempt.",
        },
        {
          key: "response",
          title: "Gateway response, exactly as eTreasury sent it" + (log.gatewayResponseReconstructed ? " (rebuilt from the saved fields)" : ""),
          text: log.gatewayResponse ? this.pretty(log.gatewayResponse) : "",
          empty: "There is no gateway response for this attempt.",
        },
        {
          key: "challan",
          title: "Challan request we sent to eTreasury",
          text: log.challanRequest ? this.pretty(log.challanRequest) : "",
          empty: "No challan request was saved for this attempt.",
        },
      ];
    },
    lookupText() {
      return this.etreasuryLog && this.etreasuryLog.lookup ? this.pretty(this.etreasuryLog.lookup) : "";
    },
    tabs() {
      return TABS;
    },
    hasFilters() {
      return !!((this.search && this.search.trim()) || this.fromDate || this.toDate);
    },
    pages() {
      return Math.ceil(this.totalLength / PAGE_SIZE);
    },
  },
  watch: {
    // Paging asks the server for that page; every other change goes through reload().
    page() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    toast(text) {
      this.msg = text;
      this.showSnackBar = true;
    },
    load() {
      clearTimeout(this.searchTimer);
      // Only the newest request may fill the list.
      const requestNo = ++this.requestNo;
      this.loading = true;
      axios
        .post(
          "/admin/payment/log",
          {
            page: this.page,
            limit: PAGE_SIZE,
            status: this.status === "all" ? undefined : this.status,
            search: (this.search || "").trim() || undefined,
            fromDate: this.fromDate || undefined,
            toDate: this.toDate || undefined,
          },
          { headers: { token: localStorage.getItem("token") } }
        )
        .then((response) => {
          if (requestNo !== this.requestNo) return;
          this.loading = false;
          if (response.data && response.data.status) {
            this.rows = response.data.data || [];
            this.totalLength = response.data.totalLength || 0;
            this.counts = response.data.counts || {};
          } else {
            this.rows = [];
            this.totalLength = 0;
            this.toast((response.data && response.data.msg) || "Could not load the payment log.");
          }
        })
        .catch((err) => {
          if (requestNo !== this.requestNo) return;
          this.loading = false;
          this.rows = [];
          this.totalLength = 0;
          this.toast("Could not load the payment log. Please try again.");
          console.log(err);
        });
    },
    // A new search, date or tab starts again from page 1.
    reload() {
      if (this.page !== 1) this.page = 1; // the watcher loads
      else this.load();
    },
    selectTab(key) {
      this.status = key;
      this.reload();
    },
    // Typing waits a moment so the list reloads once, not on every key.
    searchChanged() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.reload, 400);
    },
    clearSearch() {
      this.search = "";
      this.reload();
    },
    clearFilters() {
      this.search = "";
      this.fromDate = null;
      this.toDate = null;
      this.reload();
    },

    tabCount(key) {
      const c = this.counts[key === "all" ? "all" : key === "Attention" ? "attention" : key];
      return c ? c.count : 0;
    },
    tabAmount(key) {
      const c = this.counts[key === "all" ? "all" : key];
      return c ? c.amount : 0;
    },

    tone(r) {
      return STATUS_TONE[r.status] || "neutral";
    },
    statusLabel(r) {
      return STATUS_LABEL[r.status] || r.status || "—";
    },
    stageLabel(stage) {
      return STAGE_LABEL[stage] || stage || "";
    },
    isUnreadable(r) {
      return r.failureStage === "CallbackUnreadable";
    },
    // Why it failed: what we recorded, or else what eTreasury itself said.
    reasonOf(r) {
      if (r.failureReason) return r.failureReason;
      if (r.status === "Failed") return r.REMARKS || r.REMARK_STATUS || "eTreasury reported the payment as not successful.";
      return "";
    },
    hasGatewayReply(r) {
      return !!(r.gatewayCallbackAt || r.GRN || r.CIN || r.gatewayStatus);
    },
    gatewayStatusLabel(s) {
      if (s === "Y") return "Y (paid)";
      if (s === "N") return "N (not paid)";
      return s || "—";
    },
    timeline(r) {
      const steps = [];
      const add = (label, at, bad) => {
        if (at) steps.push({ label, at: this.formatDateTime(at), bad: !!bad });
      };
      add("Payment started", r.paymentInitiateDate || r.created_at);
      add("Challan generated", r.challanGeneratedAt);
      add("eTreasury replied", r.gatewayCallbackAt, r.status === "Failed");
      add("Payment completed", r.paymentCompleteDate);
      if (r.status === "Cancelled") add("Replaced by a newer attempt", r.updated_at);
      if (r.status === "Failed" && !r.gatewayCallbackAt) add("Failed", r.updated_at, true);
      return steps;
    },

    // Shows the row at once, then fetches its eTreasury log.
    openDetails(r) {
      this.detail = r;
      this.etreasuryLog = null;
      this.logError = "";
      this.logLoading = true;
      this.detailOpen = true;
      this.fetchDetail(false).then((data) => {
        if (!this.detail || this.detail._id !== r._id) return;
        this.logLoading = false;
        if (data) this.detail = data.data;
      });
    },
    // lookup = true also asks eTreasury live about this payment.
    fetchDetail(lookup) {
      const id = this.detail._id;
      return axios
        .post("/admin/payment/log/detail", { id, lookup: lookup || undefined }, { headers: { token: localStorage.getItem("token") } })
        .then((response) => {
          if (!this.detail || this.detail._id !== id) return null;
          if (response.data && response.data.status) {
            this.etreasuryLog = response.data.etreasuryLog;
            return response.data;
          }
          this.logError = (response.data && response.data.msg) || "Could not load the eTreasury log.";
          return null;
        })
        .catch((err) => {
          console.log(err);
          if (this.detail && this.detail._id === id) this.logError = "Could not load the eTreasury log. Please try again.";
          return null;
        });
    },
    askEtreasury() {
      if (this.lookupLoading || !this.detail) return;
      this.lookupLoading = true;
      axios
        .post("/admin/payment/log/detail", { id: this.detail._id, lookup: true }, { headers: { token: localStorage.getItem("token") } })
        .then((response) => {
          if (response.data && response.data.status && this.etreasuryLog) {
            this.$set(this.etreasuryLog, "lookup", response.data.etreasuryLog.lookup);
          } else {
            this.toast((response.data && response.data.msg) || "Could not ask eTreasury.");
          }
        })
        .catch((err) => {
          console.log(err);
          this.toast("Could not ask eTreasury. Please try again.");
        })
        .then(() => {
          this.lookupLoading = false;
        });
    },
    copy(text) {
      const done = () => this.toast("Copied.");
      const fallback = () => {
        const area = document.createElement("textarea");
        area.value = text;
        area.style.position = "fixed";
        area.style.opacity = "0";
        document.body.appendChild(area);
        area.select();
        try {
          document.execCommand("copy");
          done();
        } catch (_) {
          this.toast("Could not copy. Select the text and copy it by hand.");
        }
        document.body.removeChild(area);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done, fallback);
      else fallback();
    },
    // The whole attempt, for keeping or sending on: the saved record, the eTreasury log, and any lookup made.
    downloadLog() {
      if (!this.etreasuryLog || !this.detail) return;
      const blob = new Blob([JSON.stringify({ payment: this.detail, etreasuryLog: this.etreasuryLog }, null, 2)], { type: "application/json" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "payment-log-" + (this.detail.departmentId || this.detail._id) + ".json";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    },
    openApplication(r) {
      this.detailOpen = false;
      this.$router.push({ path: "/AdminFilmDetailedView", query: { id: r.appid._id } });
    },

    money(value) {
      return Number(value || 0).toLocaleString("en-IN");
    },
    formatDay(value) {
      return value ? moment(value).format("D MMM YYYY") : "";
    },
    formatTime(value) {
      return value ? moment(value).format("hh:mm A") : "";
    },
    formatDateTime(value) {
      return value ? moment(value).format("D MMM YYYY, hh:mm A") : "";
    },
    pretty(value) {
      if (typeof value === "string") return value;
      try {
        return JSON.stringify(value, null, 2);
      } catch (_) {
        return String(value);
      }
    },
  },
};
</script>

<style scoped>
.log-tabs {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}

.log-tab {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 14px;
  text-align: left;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid var(--line);
  cursor: pointer;
  font: inherit;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.log-tab:hover {
  border-color: var(--brand-dark);
}

.log-tab:focus-visible {
  outline: 2px solid var(--brand-dark);
  outline-offset: 2px;
}

.log-tab--active {
  border-color: var(--brand-dark);
  box-shadow: 0 0 0 2px var(--brand-dark) inset;
}

.log-tab-label {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--ink-muted);
}

.log-tab-count {
  font-size: 22px;
  font-weight: 800;
  color: var(--brand-dark);
  font-variant-numeric: tabular-nums;
}

.log-tab--danger .log-tab-count {
  color: #b91c1c;
}

.log-tab-amount {
  font-size: 12px;
  color: var(--ink-muted);
  font-variant-numeric: tabular-nums;
}

.pay-amount {
  font-weight: 800;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.status-cell {
  display: grid;
  gap: 6px;
  justify-items: start;
}

.fail-reason {
  display: grid;
  gap: 1px;
  max-width: 320px;
  font-size: 12.5px;
  line-height: 1.35;
  color: #7f1d1d;
}

.fail-reason--warn {
  color: #92400e;
}

.fail-stage {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.fail-text {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.ref-list {
  display: grid;
  gap: 2px;
  font-size: 13px;
}

.ref-key {
  display: inline-block;
  min-width: 58px;
  margin-right: 6px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: var(--ink-muted);
}

.ref-val,
.mono {
  font-family: ui-monospace, "Cascadia Mono", Consolas, monospace;
  color: #1e293b;
  word-break: break-all;
}

/* ── Details dialog ── */
.detail-title {
  align-items: center;
}

.detail-heading {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--ink-muted);
}

.detail-sub {
  font-size: 18px;
  font-weight: 800;
  font-family: ui-monospace, "Cascadia Mono", Consolas, monospace;
}

.detail-body {
  padding-top: 16px !important;
}

.fail-box {
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #7f1d1d;
  font-size: 14px;
  word-break: break-word;
}

.fail-box--warn {
  background: #fffbeb;
  border-color: #fde68a;
  color: #92400e;
}

.fail-box-title {
  margin-bottom: 2px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.fail-box-hint {
  margin-top: 8px;
  font-weight: 700;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px 28px;
}

.detail-grid h3 {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--ink-muted);
}

.detail-grid dl {
  display: grid;
  grid-template-columns: minmax(110px, auto) 1fr;
  gap: 4px 12px;
  margin: 0;
  font-size: 14px;
}

.detail-grid dt {
  color: var(--ink-muted);
}

.detail-grid dd {
  margin: 0;
  font-weight: 600;
  word-break: break-word;
}

.timeline {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 6px;
  font-size: 14px;
}

.timeline li {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding-left: 12px;
  border-left: 3px solid #86efac;
}

.timeline li.timeline--bad {
  border-left-color: #f87171;
}

.timeline-label {
  font-weight: 700;
}

.timeline-time {
  color: var(--ink-muted);
  white-space: nowrap;
}

.detail-panels {
  margin-top: 18px;
}

.log-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 24px 0 10px;
  padding-top: 16px;
  border-top: 1px solid var(--line);
}

.log-head-title {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
}

.log-blocks {
  display: grid;
  gap: 14px;
}

.log-block-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.log-block-head h4 {
  flex: 1;
  margin: 0;
  font-size: 13px;
  font-weight: 800;
}

.log-empty {
  padding: 8px 2px;
  font-size: 13px;
}

.raw {
  margin: 0;
  padding: 10px 12px;
  max-height: 260px;
  overflow: auto;
  border-radius: 10px;
  background: #f1f5f9;
  font-family: ui-monospace, "Cascadia Mono", Consolas, monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
}

@media (max-width: 1100px) {
  .log-tabs {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .log-tabs {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .detail-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .ref-list,
  .status-cell {
    justify-items: end;
  }

  .fail-reason {
    text-align: right;
  }
}
</style>
